import {
  FLASHCARD_REVIEWS_BATCH_MAX,
  type FlashcardReviewInput,
  FlashcardReviewsResponse,
  MeResponse,
} from "@edu/shared";
import { call } from "../../lib/api.ts";
import {
  batchesOf,
  flashcardsDbName,
  flushDecision,
  markAttempt,
  maySend,
  pendingReviews,
  reviewInputsOf,
  settleReviews,
  staleSent,
  verifyOutcome,
  type QueuedReview,
} from "./review-queue.ts";
import {
  deleteReviews,
  forgetDecks,
  listDbNames,
  pruneForeignFlashcards,
  putReviews,
  readQueue,
} from "./review-store.ts";

/**
 * Досылка очереди повторений (7.3-7.4 `docs/flashcards-design.md`).
 *
 * ⚠️ **Стартовый чанк и без React**, по образцу очереди заметок
 * (`features/feedback/queue.ts`): `signOut` зовёт досылку напрямую, а провайдер
 * сессии про фичи ниже себя знать не может. Импорт только корневого
 * `@edu/shared`; `@edu/shared/flashcards` и `ts-fsrs` сюда не ходят никогда.
 *
 * Поводы досылки: оценка (сразу), событие `online`, возвращение вкладки, вход
 * того же человека (то есть «следующее открытие») и таймер с отступом, пока в
 * очереди есть недоставленное.
 *
 * ⚠️ **Отправляется только очередь ТЕКУЩЕГО пользователя.** Хозяина ставит
 * `lib/session.tsx` (`setFlashcardsUser`), в просмотре кабинета его нет вовсе:
 * сессия там чужая, и оценки ушли бы под чужим именем.
 */

/** Пауза после неудачи: 30 с, минута, 2, 5 минут и дальше по 5. */
const BACKOFF_MS = [30_000, 60_000, 120_000, 300_000];
/** Потолок ожидания одного запроса: тело маленькое, а выход ждёт ответа. */
const SEND_TIMEOUT_MS = 15_000;

let currentUser: string | null = null;
let started = false;
/** Строки, которые хранилище не приняло: живут, пока открыта вкладка. */
const memory = new Map<string, QueuedReview[]>();
let strikes = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let active: Promise<void> | null = null;
let again = false;
/** Последняя досылка не дошла до сервера: экран говорит «отправим позже». */
let offline = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

/** Подписка экрана тренажёра на изменения очереди. */
export function subscribeReviews(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Не дошла ли последняя досылка. */
export function reviewsOffline(): boolean {
  return offline;
}

/**
 * Кто в сессии. Зовёт `lib/session.tsx` на каждое изменение; `null` при выходе,
 * без сессии и в просмотре кабинета. Вход это и есть «следующее открытие»:
 * очередь досылается, а кэш чужих баз этого браузера стирается.
 */
export function setFlashcardsUser(userId: string | null): void {
  if (userId === currentUser) return;
  currentUser = userId;
  strikes = 0;
  offline = false;
  clearTimer();
  notify();
  if (userId === null) return;
  start();
  void pruneForeignFlashcards(userId);
  flushSoon(true);
}

function start(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", () => flushSoon(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flushSoon(true);
  });
}

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/** Вся очередь пользователя: хранилище плюс то, что оно не приняло. */
export async function readReviews(userId: string): Promise<QueuedReview[]> {
  const stored = (await readQueue(userId)) ?? [];
  const mem = memory.get(userId) ?? [];
  const seen = new Set(stored.map((r) => r.id));
  return [...stored, ...mem.filter((r) => !seen.has(r.id))];
}

/**
 * Положить оценку в очередь в момент нажатия (7.3). `memory` значит «хранилище
 * не приняло»: оценка уедет, только пока вкладка открыта, и экран говорит это.
 */
export async function addReview(userId: string, input: FlashcardReviewInput): Promise<"stored" | "memory"> {
  const row: QueuedReview = { ...input, sentAt: null };
  const ok = await putReviews(userId, [row]);
  if (!ok) memory.set(userId, [...(memory.get(userId) ?? []), row]);
  notify();
  flushSoon();
  return ok ? "stored" : "memory";
}

/**
 * Досылка без ожидания результата. Залпа нет: идущий круг отмечает «ещё раз».
 *
 * ⚠️ После неудачи оценка досылку НЕ торопит, её ждёт таймер с отступом: без
 * сети каждое нажатие «Помню» было бы запросом в никуда. Торопят только
 * события, которые говорят о сети или о человеке (`online`, возвращение
 * вкладки, вход), это `force`.
 */
export function flushSoon(force = false): void {
  if (!force && offline && timer !== null) return;
  void flushReviews();
}

/**
 * Досылка всей очереди текущего пользователя. `true`, если недоставленного не
 * осталось. Не бросает никогда.
 */
export async function flushReviews(): Promise<boolean> {
  if (active !== null) {
    again = true;
    await active;
    return lastPending === 0;
  }
  active = (async () => {
    do {
      again = false;
      await flushOnce();
    } while (again && !offline && currentUser !== null);
  })();
  try {
    await active;
  } finally {
    active = null;
    for (const fn of idle.splice(0)) fn();
  }
  return lastPending === 0;
}

let lastPending = 0;
/** Сколько POST досылки начато за жизнь вкладки: по нему экран узнаёт пересечение. */
let postEpoch = 0;
let postsInFlight = 0;
const idle: (() => void)[] = [];

/**
 * Дождаться, пока досылка не идёт (7.3). Экран тренажёра читает колоды и
 * очередь только в тишине: колода, прочитанная во время POST, может уже
 * содержать строки, которые очередь ещё считает недоставленными, и оценка
 * легла бы дважды.
 */
export function whenFlushIdle(): Promise<void> {
  return active === null ? Promise.resolve() : new Promise((resolve) => idle.push(resolve));
}

/**
 * Метка «шла ли досылка»: экран сравнивает её до и после чтения колоды, и
 * колоду, чтение которой пересеклось с POST, перечитывает.
 */
export function flushMark(): string {
  return `${String(postEpoch)}:${String(postsInFlight)}`;
}

async function flushOnce(): Promise<void> {
  const owner = currentUser;
  if (owner === null) return;
  clearTimer();
  if (!(await hasDb(owner)) && (memory.get(owner) ?? []).length === 0) {
    lastPending = 0;
    return;
  }
  let rows = await readReviews(owner);
  const stale = staleSent(rows, Date.now());
  if (stale.length > 0) {
    await deleteReviews(owner, stale);
    settleMemory(owner, [], stale);
    rows = rows.filter((r) => !stale.includes(r.id));
  }
  const pending = pendingReviews(rows);
  lastPending = pending.length;
  if (pending.length === 0) {
    offline = false;
    notify();
    return;
  }
  for (const raw of batchesOf(pending, FLASHCARD_REVIEWS_BATCH_MAX)) {
    if (!maySend(owner, currentUser)) return;
    const batch = markAttempt(raw, Date.now());
    await keep(owner, batch.filter((r, i) => r !== raw[i]));
    const result = await post(batch);
    const decision = flushDecision(batch, result);
    // Вся пачка «не ваша»: сессия могла смениться в соседней вкладке. Строки не
    // удаляются, пока не подтверждено, что в сессии тот же человек
    const verified = decision === "verify" ? verifyOutcome(owner, await whoIsSigned()) : "same";
    if (verified === "other") {
      setFlashcardsUser(null);
      return;
    }
    if (decision === "keep" || verified === "retry" || !result.ok) {
      offline = true;
      strikes += 1;
      plan();
      notify();
      return;
    }
    rows = rows.map((r) => batch.find((b) => b.id === r.id) ?? r);
    const settled = settleReviews(rows, result.response, Date.now());
    await keep(owner, settled.put);
    await deleteReviews(owner, settled.removed);
    settleMemory(owner, [], settled.removed);
    rows = settled.rows;
  }
  strikes = 0;
  offline = false;
  lastPending = pendingReviews(rows).length;
  if (lastPending > 0) plan();
  notify();
}

/**
 * Один POST пачки. `silent401`, как у очереди заметок: 401 здесь временный
 * (сессию могли продлить в соседней вкладке), и выбрасывать человека на экран
 * входа из фоновой досылки нельзя. Очередь при нём просто ждёт.
 */
async function post(
  batch: readonly QueuedReview[],
): Promise<{ ok: true; response: FlashcardReviewsResponse } | { ok: false }> {
  postEpoch += 1;
  postsInFlight += 1;
  try {
    const response = await call(
      "/v1/flashcards/reviews",
      { method: "POST", body: { reviews: reviewInputsOf(batch) }, timeoutMs: SEND_TIMEOUT_MS, silent401: true },
      FlashcardReviewsResponse,
    );
    return { ok: true, response };
  } catch {
    return { ok: false };
  } finally {
    postsInFlight -= 1;
  }
}

/**
 * Кто в сессии, отдельным запросом с тем же таймаутом и без общего обработчика
 * 401: проверка из фоновой досылки не должна выбрасывать человека на вход.
 */
async function whoIsSigned(): Promise<{ ok: true; userId: string } | { ok: false }> {
  try {
    const me = await call("/v1/auth/me", { silent401: true, timeoutMs: SEND_TIMEOUT_MS }, MeResponse);
    return { ok: true, userId: me.userId };
  } catch {
    return { ok: false };
  }
}

/** Записать строки туда, где они живут: в хранилище или в память вкладки. */
async function keep(userId: string, rows: readonly QueuedReview[]): Promise<void> {
  if (rows.length === 0) return;
  const mem = memory.get(userId) ?? [];
  const inMemory = new Set(mem.map((r) => r.id));
  await putReviews(userId, rows.filter((r) => !inMemory.has(r.id)));
  settleMemory(userId, rows.filter((r) => inMemory.has(r.id)), []);
}

/**
 * Есть ли база у человека. Не плодим пустую базу у того, кто ни разу не
 * тренировался: открытие базы её создаёт. Браузер без перечисления баз
 * отвечает «есть», и тогда пустая база заводится, это дёшево.
 */
async function hasDb(userId: string): Promise<boolean> {
  const names = await listDbNames();
  return names === null || names.includes(flashcardsDbName(userId));
}

function settleMemory(userId: string, put: readonly QueuedReview[], removed: readonly string[]): void {
  const mem = memory.get(userId);
  if (mem === undefined) return;
  const next = new Map(put.map((r) => [r.id, r]));
  memory.set(
    userId,
    mem.filter((r) => !removed.includes(r.id)).map((r) => next.get(r.id) ?? r),
  );
}

function plan(): void {
  clearTimer();
  if (currentUser === null) return;
  const ms = BACKOFF_MS[Math.min(strikes, BACKOFF_MS.length) - 1] ?? 30_000;
  timer = setTimeout(() => flushSoon(true), ms);
}

/**
 * Выход (7.4): досылка без вопроса, ДО `api.signOut()` (иначе cookie уже нет),
 * неудача выход не отменяет и очередь не трогает; затем стирается только кэш
 * колод. Не бросает никогда.
 */
export async function flushFlashcardsBeforeSignOut(): Promise<void> {
  const userId = currentUser;
  if (userId === null) return;
  try {
    await flushReviews();
    if (await hasDb(userId)) await forgetDecks(userId);
  } catch {
    /* выход важнее: очередь осталась на диске */
  }
}
